import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, Sparkles, X } from "lucide-react";
import { api, ApiError, type AskAccess } from "../../lib/api";
import { cn, useDebouncedValue } from "../../lib/utils";
import { Badge, Input, Skeleton, Switch } from "../../components/ui/primitives";

// Super users: who has Ask (docs/customer-ask.md, "Who can ask"). It's off for every customer until
// turned on here, per person; staff always have it. Every change is logged and shown per person.
// Usage is that person's own asks this calendar month (UTC), all four token counts added up.

const shortDate = (at: string) => new Date(at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

export function AskAccessPage() {
  const [q, setQ] = useState("");
  const query = useDebouncedValue(q.trim(), 300);
  const [selected, setSelected] = useState<string>();
  const people = useQuery({ queryKey: ["ask-access"], queryFn: api.askAccessList, retry: false });
  const found = useQuery({ queryKey: ["ask-access-search", query], queryFn: () => api.askAccessSearch(query), enabled: Boolean(query), retry: false });

  const forbidden = people.error instanceof ApiError && people.error.status === 403;
  return (
    <div className="mx-auto max-w-[1100px] px-6 pb-16">
      <header className="sticky top-0 z-20 -mx-6 mb-4 border-b border-border bg-background/85 px-6 py-3 backdrop-blur">
        <h1 className="flex items-center gap-2 text-lg font-semibold tracking-tight"><Sparkles className="size-4 text-subtle" /> Ask access</h1>
        <p className="mt-1 text-sm text-muted">
          Ask is off for every customer until you turn it on for them here. Staff always have it.
          Customers can't use Ask yet, so for now this only records who will have it.
        </p>
      </header>

      {forbidden ? (
        <p className="text-sm text-bad">Only super users can change who has Ask.</p>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
          <div className="space-y-6">
            <section>
              <div className="relative max-w-md">
                <Search className="pointer-events-none absolute left-2.5 top-2 size-4 text-subtle" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a customer: name, email, user or location ID" aria-label="Find a customer" className="pl-8" />
              </div>
              {query && (
                <div className="mt-2 rounded-[14px] border border-border bg-surface">
                  {found.isLoading ? (
                    <div className="p-3"><Skeleton className="h-9" /></div>
                  ) : found.error ? (
                    <p className="p-3 text-sm text-bad">{found.error.message}</p>
                  ) : !found.data?.length ? (
                    <p className="p-3 text-sm text-muted">No customer matches “{query}”. They need to have signed in here once.</p>
                  ) : (
                    found.data.map((u) => (
                      <PersonRow
                        key={u.userId}
                        userId={u.userId}
                        name={u.name}
                        sub={`${u.email ? `${u.email} · ` : ""}${u.orgId} · ${u.locationCount} location${u.locationCount === 1 ? "" : "s"}`}
                        enabled={u.enabled}
                        onSelect={() => setSelected(u.userId)}
                      />
                    ))
                  )}
                </div>
              )}
            </section>

            <section>
              <h2 className="mb-2 text-sm font-medium">People with Ask on</h2>
              {people.isLoading ? (
                <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-9" />)}</div>
              ) : people.error ? (
                <p className="text-sm text-bad">{people.error.message}</p>
              ) : !people.data?.length ? (
                <p className="text-sm text-muted">No customer has Ask yet. Find someone above to turn it on.</p>
              ) : (
                <div className="rounded-[14px] border border-border bg-surface">
                  {people.data.map((a) => (
                    <PersonRow
                      key={a.userId}
                      userId={a.userId}
                      name={a.userName ?? a.userEmail ?? a.userId}
                      sub={`${a.userEmail ? `${a.userEmail} · ` : ""}${a.orgId} · ${usageLine(a)}`}
                      enabled={a.enabled}
                      onSelect={() => setSelected(a.userId)}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>

          {selected && <History userId={selected} onClose={() => setSelected(undefined)} />}
        </div>
      )}
    </div>
  );
}

const usageLine = (a: AskAccess) =>
  `${a.thisMonth.asks.toLocaleString()} ask${a.thisMonth.asks === 1 ? "" : "s"} this month · ${a.thisMonth.tokens.toLocaleString()} tokens`;

/** One person, with their switch. Clicking the name shows their history. */
function PersonRow({ userId, name, sub, enabled, onSelect }: { userId: string; name: string; sub: string; enabled: boolean; onSelect: () => void }) {
  const queryClient = useQueryClient();
  const set = useMutation({
    mutationFn: (on: boolean) => api.setAskAccess(userId, on),
    onSettled: () => queryClient.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith("ask-access") }),
  });
  const id = `ask-${userId}`;
  return (
    <div className="flex items-center gap-3 border-b border-border px-3 py-2 last:border-b-0">
      <button onClick={onSelect} className="min-w-0 flex-1 text-left" title="Show history">
        <span className="block truncate text-sm hover:text-accent hover:underline">{name}</span>
        <span className="block truncate text-xs text-subtle">{sub}</span>
      </button>
      {set.error && <span className="text-xs text-bad">{set.error.message}</span>}
      <label htmlFor={id} className="text-xs text-muted">{(set.isPending ? set.variables : enabled) ? "On" : "Off"}</label>
      <Switch id={id} checked={set.isPending ? Boolean(set.variables) : enabled} onCheckedChange={(on) => set.mutate(on)} />
    </div>
  );
}

function History({ userId, onClose }: { userId: string; onClose: () => void }) {
  const detail = useQuery({ queryKey: ["ask-access-detail", userId], queryFn: () => api.askAccess(userId), retry: false });
  const a = detail.data?.access;
  return (
    <aside className="h-fit rounded-[14px] border border-border bg-surface p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="truncate text-sm font-medium">{a?.userName ?? a?.userEmail ?? userId}</h2>
          <p className="truncate text-xs text-subtle">{userId}</p>
        </div>
        <button onClick={onClose} aria-label="Close" className="rounded p-0.5 text-muted hover:bg-surface-2"><X className="size-4" /></button>
      </div>
      {detail.isLoading ? (
        <Skeleton className="mt-3 h-20" />
      ) : detail.error ? (
        <p className="mt-3 text-sm text-bad">{detail.error.message}</p>
      ) : (
        <>
          <p className="mt-3 text-sm">
            <Badge tone={detail.data!.enabled ? "good" : "neutral"}>{detail.data!.enabled ? "On" : "Off"}</Badge>
            {a && <span className="ml-2 text-xs text-muted">{usageLine(a)}</span>}
          </p>
          <h3 className="mt-4 text-xs font-medium text-muted">History</h3>
          {!detail.data!.changes.length ? (
            <p className="mt-1 text-xs text-subtle">Never changed: Ask has always been off.</p>
          ) : (
            <ol className="mt-1 space-y-1.5">
              {detail.data!.changes.map((ch) => (
                <li key={ch.id} className="text-xs">
                  <span className={cn("font-medium", ch.enabled ? "text-good" : "text-muted")}>{ch.enabled ? "Turned on" : "Turned off"}</span>
                  {" by "}{ch.changedByEmail ?? ch.changedBy}
                  <span className="block text-subtle" title={ch.at}>{shortDate(ch.at)}</span>
                </li>
              ))}
            </ol>
          )}
        </>
      )}
    </aside>
  );
}
